import React, { ReactElement } from 'react';

import IconPlayButtonBlackOutline from '@backrs_ui/common-ui/src/assets/images/play_button_black_outline.svg';

import { Box, SxProps, Theme } from '@mui/material';
import ReactPlayer from 'react-player';

const YOUTUBE_REGEX =
  /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)(\/(?:[\w-]+\?v=|embed\/|v\/)?)([\w-]+)(\S+)?$/;

const TED_TALKS_REGEX = /^(https?:\/\/)?(www\.)?(ted\.com)(\/talks\/)([\w-]+)(\S+)?$/;

/**
 * Maps the media URL to the appropriate iframe source. As of now, only YouTube and TED Talks are supported, but other sources can be added in the future.
 */
function mediaUrliFrameMapper(url: string): string | undefined {
  const youtubeVideoId = url.match(YOUTUBE_REGEX)?.[5];

  if (youtubeVideoId) {
    return `https://www.youtube.com/embed/${youtubeVideoId}`;
  }

  const tedTalksVideoId = url.match(TED_TALKS_REGEX)?.[5];

  if (tedTalksVideoId) {
    return `https://embed.ted.com/talks/${tedTalksVideoId}`;
  }

  return undefined;
}

type NuggetProps = Readonly<{
  variant:
    | {
        type: 'SMALL';
        url: string;
        previewImage?: string;
        isInternal?: boolean;
      }
    | {
        type: 'LARGE';
        url: string;
      };
}>;

export const NuggetVideo = ({ variant }: NuggetProps): ReactElement => {
  if (variant.type === 'SMALL') {
    if (variant.isInternal) {
      // if the video is internal, we will use the video thumbnail as the preview image
      // we wont enable controls though
      return (
        <Box position="relative">
          <Box
            component="img"
            sx={playButtonContainerStyle}
            src={IconPlayButtonBlackOutline}
            width={56}
            height={56}
          />
          <ReactPlayer
            style={smallVideoReactPlayerStyle}
            width="100%"
            height="100%"
            url={[{ src: variant.url, type: 'video/mp4' }]}
            config={{
              file: {
                attributes: {
                  style: {
                    objectFit: 'cover',
                    width: '100%',
                    height: '100%',
                  },
                },
              },
            }}
          />
        </Box>
      );
    }

    const youtubeVideoId = variant.url.match(YOUTUBE_REGEX)?.[5];

    // If the video is a YouTube video, we will use the YouTube thumbnail as the preview image in the case that we don't have a custom preview image
    const youtubeThumbnailUrl = youtubeVideoId
      ? `https://img.youtube.com/vi/${youtubeVideoId}/0.jpg`
      : undefined;

    const previewImage = variant.previewImage || youtubeThumbnailUrl || '';

    if (!previewImage) {
      return <></>;
    }

    return (
      <Box position="relative">
        <Box
          component="img"
          sx={playButtonContainerStyle}
          src={IconPlayButtonBlackOutline}
          width={56}
          height={56}
        />
        {variant.isInternal ? (
          <Box sx={{ ...videoThumbnailStyle, backgroundImage: `url(${previewImage})` }}>
            {previewImage ? (
              <></>
            ) : (
              <video width="100%" height="100%" src={variant.url} style={{ objectFit: 'cover' }} />
            )}
          </Box>
        ) : (
          <Box
            sx={{
              ...videoThumbnailStyle,
              backgroundImage: `url(${previewImage})`,
            }}
          />
        )}
      </Box>
    );
  }

  const dialogVideoIframeSource = mediaUrliFrameMapper(variant.url);

  return (
    <Box sx={videoPlayerStyle}>
      {dialogVideoIframeSource ? (
        <Box sx={externalIFrameContainerStyle}>
          <iframe
            width="100%"
            height="100%"
            src={dialogVideoIframeSource}
            title="Backrs video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </Box>
      ) : (
        <Box>
          <ReactPlayer controls width="100%" url={[{ src: variant.url, type: 'video/mp4' }]} />
        </Box>
      )}
    </Box>
  );
};

const videoThumbnailStyle: SxProps<Theme> = {
  width: '100%',
  overflow: 'hidden',
  display: 'block',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  maxHeight: '200px',
  minHeight: '200px',
};

const externalIFrameContainerStyle: SxProps<Theme> = {
  height: '500px',
  '@media (max-width: 756px)': {
    height: '300px',
  },
  '@media (max-width: 500px)': {
    height: 'unset',
  },
};

const videoPlayerStyle: SxProps<Theme> = {
  width: '100%',
  mt: 3,
};

const playButtonContainerStyle: SxProps<Theme> = {
  position: 'absolute',
  bottom: '12%',
  left: '5%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  zIndex: 1,
};

const smallVideoReactPlayerStyle: React.CSSProperties = {
  width: '100%',
  overflow: 'hidden',
  display: 'block',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  maxHeight: '200px',
  minHeight: '200px',
};
